import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import Grid from '@material-ui/core/Grid';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FaEdit } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';
import { RiArrowLeftSLine, RiCheckboxCircleLine, RiCloseCircleLine, RiDeleteBinLine, RiUserAddLine } from 'react-icons/ri';

import { AxiosError } from 'axios';
import Input from '../../../../components/Input';
import { useToast } from '../../../../hooks/toast';

import api from '../../../../services/api';
import getValidationErrors from '../../../../utils/getValidationsErrors';

import { Checkbox, FormControlLabel } from '@material-ui/core';
import { BiSearchAlt } from 'react-icons/bi';
import { FiAlertCircle } from 'react-icons/fi';
import Button from '../../../../components/Button';
import PageTitle from '../../../../components/PageTitle';
import Select from '../../../../components/Select';
import { useAuth } from '../../../../hooks/auth';
import { IClient } from '../../../../interfaces/IClient';
import { IPermission } from '../../../../interfaces/IPermission';
import { IRole, IRoleList } from '../../../../interfaces/IRole';
import * as S from './styles';


interface ISearchRolesParams {
  name?: string;
  client_id?: string;
}

interface INewRole {
  name: string;
  description: string;
  clientId: string;
  permissions: IPermission[];
}

interface IPermissionCombo {
  id: string;
  name: string;
  description: string;
}

const Roles: React.FC = () => {
  const RoleFormRef = useRef<FormHandles>(null);
  const RoleUpdateFormRef = useRef<FormHandles>(null);
  const searchFormRef = useRef<FormHandles>(null);
  const { user: authUser } = useAuth();
  const { addToast } = useToast();

  const [loader, setLoader] = useState(false);
  const [roleList, setRoleList] = useState<IRoleList[]>([] as IRoleList[]);
  const [clientList, setClientList] = useState<IClient[]>([] as IClient[]);
  const [permissionList, setPermissionList] = useState<IPermissionCombo[]>([] as IPermissionCombo[]);
  const [updateRole, setUpdateRole] = useState<IRole>();
  const [createRole, setCreateRole] = useState<IRole>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [register, setRegister] = useState(false);
  const [isActiveRole, setActiveRole] = useState(true);
  const [permissionValidationError, setPermissionValidationError] = useState<string>();

  const canCreate: boolean = authUser.sysadmin || (authUser.permissions?.includes("|role-create|") || false);
  const canUpdate: boolean =authUser.sysadmin ||(authUser.permissions?.includes("|role-update|") || false);
  const canDelete: boolean = authUser.sysadmin || (authUser.permissions?.includes("|role-delete|") || false);


  // Inicialize os estados dos switches com base nas permissões
  const initialSwitchesState = permissionList.reduce((acc: Record<string, boolean>, permission) => { 
    if (permission.name.toLowerCase() === "usuários" && permission.description.toLowerCase() === "visualizar") {
      acc[permission.id] = true; 
    } else
      acc[permission.id] = false; 
    return acc; 
  }, 
  {}); 

  const isClientAdmin = authUser.sysadmin || authUser.client_admin

  const [switchesState, setSwitchesState] = useState<Record<string, boolean>>(initialSwitchesState);

  const handleChange = useCallback((permission: IPermissionCombo, role?:IRole) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    setSwitchesState(prevState => ({ ...prevState, [permission.id]: checked }));
    if (role) {
      if (checked) {
        role.permissions.push(permission);

        if (permission.name.toLowerCase().includes('nível')) {
          role.permissions.forEach((p: IPermission) => {
            if (p.name.toLowerCase().includes('nível') && p.id !== permission.id) {
              role.permissions = role.permissions.filter(q => q.id !== p.id);
              setSwitchesState(prevState => ({ ...prevState, [p.id]: false }));
            }
          })
        }
      } else {
        role.permissions = role.permissions.filter(p => p.id !== permission.id);
      }
      if (register) 
        setCreateRole(role);
      else 
        setUpdateRole(role);
      
    }
  }, [register, setCreateRole, setUpdateRole, setSwitchesState]);

  const activeRoleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setActiveRole(!isActiveRole);
    if (updateRole) updateRole.active = event.target.checked;
  },[isActiveRole, updateRole]);

  const searchRolesList = useCallback(
    async (params: ISearchRolesParams) => {
      try {
        const { data } = await api.get('roles', { params });

        return setRoleList(data);
      } catch (error) {
        return addToast({
          type: 'error',
          title: 'Erro ao buscar Perfis',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast],
  );

  const getClientList = useCallback(async () => {
    try {
      const { data } = await api.get('clients');
      return setClientList(data);
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar Clientes',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);

  const getPermissionList = useCallback(async () => {
    try {
      const { data } = await api.get('permissions/tocombo');
      setPermissionList(data);
      setSwitchesState(data.reduce((acc: Record<string, boolean>, permission: IPermissionCombo) => { acc[permission.id] = false; return acc; }, {}));
      
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar permissões',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);

  const getRoleById = useCallback(
    async (id: string) => {
      try {
        const { data } = await api.get(`roles/${id}`);
        setUpdateRole(data);

        data.permissions.forEach((permission: IPermission) => {
          setSwitchesState(prevState => ({ ...prevState, [permission.id]: true }));
        });
        setActiveRole(data.active);
      } catch (error) {
        return addToast({
          type: 'error',
          title: 'Erro ao buscar perfil',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast],
  );

  const searchHandle = useCallback(async () => {
    const name = searchFormRef.current?.getFieldValue('searchRole');
    let client_id = undefined 
    
    if (isClientAdmin) {
      client_id = searchFormRef.current?.getFieldValue('searchClient');
    }
    
    try {
      await searchRolesList({ name, client_id });
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar Perfis',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, searchRolesList, isClientAdmin]);

  useEffect(() => {
    searchHandle();
    searchRolesList({});
    if (isClientAdmin) getClientList();
    getPermissionList();
  }, [searchHandle, searchRolesList, getClientList, getPermissionList, isClientAdmin]);

  useEffect(() => {
    // Foca no campo de pesquisa quando o componente é montado
    const searchInput = searchFormRef.current?.getFieldRef('searchRole');
    if (searchInput) {
      searchInput.focus();
    }
  }, []);

  const RoleFormHandler = useCallback(
    async (formData: INewRole) => {
      setLoader(true);

      try {
        RoleFormRef.current?.setErrors({});
        setPermissionValidationError(undefined); 
        
        let fieldsToValidate: any = {
          name: Yup.string().trim().required('Nome é obrigatório'),
          permissions: Yup.array().min(1, 'Selecione ao menos uma permissão')
        }
        
        if (isClientAdmin)
          fieldsToValidate.clientId = Yup.string().trim().required('Cliente é obrigatório')

        const schema = Yup.object().shape(fieldsToValidate);

        if (!formData.clientId && !isClientAdmin)
          formData.clientId = authUser.client_id

        formData.permissions = createRole?.permissions || [];

        await schema.validate(formData, {
          abortEarly: false,
        });

        const dataToSend: INewRole = formData;

        await api.post('/roles', dataToSend);

        RoleFormRef.current?.reset();
        setCreateRole(undefined);
        setRegister(false);
        searchRolesList({});

        return addToast({
          type: 'success',
          title: 'Perfil criado!',
          description: 'Perfil criado com sucesso!',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          if ("permissions" in errors)
            setPermissionValidationError(errors.permissions);
          return RoleFormRef.current?.setErrors(errors);
        }

        return addToast({
          type: 'error',
          title: 'Erro ao criar novo perfil',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        setLoader(false);
      }
    },
    [addToast, searchRolesList, createRole, authUser, isClientAdmin],
  );

  const RoleUpdateFormHandler = useCallback(
    async formData => {
      setLoader(true);
      
      try {
        RoleUpdateFormRef.current?.setErrors({});
        setPermissionValidationError(undefined);

        let fieldsToValidate: any = {
          name: Yup.string().trim().required('Nome é obrigatório'),
          permissions: Yup.array().min(1, 'Selecione ao menos uma permissão')
        }
        
        if (isClientAdmin)
          fieldsToValidate.client_id = Yup.string().trim().required('Cliente é obrigatório')

        const schema = Yup.object().shape(fieldsToValidate);

        formData.permissions = updateRole?.permissions || [];

        await schema.validate(formData, {
          abortEarly: false,
        });

        const { id, name, client_id, description} = formData;

        const dataToSend = {
          id: updateRole?.id,
          name: name || updateRole?.name,
          description: description || updateRole?.description,
          active: updateRole?.active,
          clientId: client_id || updateRole?.clientId,
          permissions: updateRole?.permissions,
        };
        
        await api.put(`/roles/${id}`, dataToSend);

        RoleUpdateFormRef.current?.reset();

        setUpdateRole(undefined);
        searchRolesList({});

        return addToast({
          type: 'success',
          title: 'Perfil atualizado!',
          description: 'Perfil atualizado com sucesso!',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          if ("permissions" in errors)
            setPermissionValidationError(errors.permissions);
          return RoleUpdateFormRef.current?.setErrors(errors);
        }

        return addToast({
          type: 'error',
          title: 'Erro ao atualizar perfil',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        setLoader(false);
      }
    },
    [addToast, updateRole, searchRolesList, isClientAdmin],
  );

  const handleDeleteRole = useCallback(async () => {
    if (!updateRole?.id) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Nenhum perfil selecionado para exclusão',
      });
      return;
    }

    try {
      await api.delete(`/roles/${updateRole.id}`);

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Perfil excluído com sucesso!',
      });

      setUpdateRole(undefined);
      searchRolesList({});
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao excluir perfil',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, updateRole, searchRolesList]);

  const confirmDeleteRole = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const RoleForm = useMemo(
    () => (
      <>
        <Form ref={RoleFormRef} onSubmit={formData => RoleFormHandler({ ...formData })}>
          <Grid container spacing={5}>
            <Grid item xs={12} sm={5}>
              <div className="form">
                <Grid container spacing={2} className="inputs">
                  <Grid item xs={12} sm={12} className={loader ? 'disabled' : 'input'}>
                    <p>Nome</p>
                    <Input name="name" type="text" />
                  </Grid>
                  {isClientAdmin && (
                    <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                      <p>Cliente</p>
                      <Select name="clientId" data={clientList} placeholder="Cliente..." />
                    </Grid>
                  )}
                  <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                    <p>Descrição</p>
                    <Input name="description" type="text" />
                  </Grid>
                </Grid>
              </div>
            </Grid>       
            <Grid item xs={12} sm={7}  >
              <Grid style={{borderBottom: '1px solid #000', marginBottom: '10px'}}>
                <h2>Permissões</h2>
                {permissionValidationError && (
                <S.PermissionError title={permissionValidationError}>
                  <FiAlertCircle color="#c53030" size={20} />
                </S.PermissionError>
              )}
              </Grid>
              
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                  <h3 style={{fontWeight:'bold'}}>Dispositivos</h3>
                  {permissionList.filter(p => p.name === "Dispositivos").map(permission => (
                    <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={switchesState[permission.id]} 
                            onChange={handleChange(permission, createRole)}
                            color="primary"
                          />
                        }
                        label={permission.description}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                  <h3 style={{fontWeight:'bold'}}>Perfil</h3>
                  {permissionList.filter(p => p.name === "Perfil").map(permission => (
                    <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={switchesState[permission.id]} 
                            onChange={handleChange(permission, createRole)}
                            color="primary"
                          />
                        }
                        label={permission.description}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                  <h3 style={{fontWeight:'bold'}}>Usuários</h3>
                  {permissionList.filter(p => p.name === "Usuários").map(permission => (
                    <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={permission.description.toLowerCase() === "visualizar" || switchesState[permission.id]} 
                            onChange={handleChange(permission, createRole)}
                            color="primary"
                            disabled={permission.description.toLowerCase() === "visualizar"}
                          />
                        }
                        label={permission.description}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                  <h3 style={{fontWeight:'bold'}}>Nível Hierárquico</h3>
                  {permissionList.filter(p => p.name === "Nível Hierárquico").map(permission => (
                    <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                      <FormControlLabel
                        key={permission.id}
                        control={
                          <Checkbox
                            checked={switchesState[permission.id]} 
                            onChange={handleChange(permission, createRole)}
                            color="primary"
                          />
                        }
                        label={permission.description}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100px', // Altura opcional, ajuste conforme necessário
              }}
            >
              {loader ? (
                <Button>Atualizando ...</Button>
              ) : (
                <>
                  <Button>
                    <RiUserAddLine size={20} />
                    Atualizar
                  </Button>
                </>

              )}
            </Grid>  
          </Grid>
        </Form>
      </>
    ),
    [loader, RoleFormHandler, permissionList, createRole, switchesState, permissionValidationError, clientList,  handleChange, isClientAdmin],
  );

  const RoleUpdateForm = useMemo(
    () => (
      <>
      <Form ref={RoleUpdateFormRef} onSubmit={formData => RoleUpdateFormHandler({ ...formData, id: updateRole?.id })}>
        <Grid container spacing={5}>
          <Grid item xs={12} sm={5}>
            <div className="form">
              <Grid container spacing={2} className="inputs">
                <Grid item xs={12} sm={12} className={loader ? 'disabled' : 'input'}>
                  <p>Nome</p>
                  <Input name="name" type="text" defaultValue={updateRole?.name} />
                </Grid>
                {isClientAdmin && (
                  <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                    <p>Cliente</p>
                    <Select name="client_id" defaultValue={updateRole?.clientId} data={clientList} placeholder="Cliente..." />
                  </Grid>
                )}
                <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                  <p>Descrição</p>
                  <Input name="description" type="text" defaultValue={updateRole?.description} max={10} />
                </Grid>
                <Grid item xs={12} className={loader ? 'disabled' : 'input'}>
                  <Checkbox name="active" checked={isActiveRole} color="primary" onChange={activeRoleChange} /> Ativo?
                </Grid>
              </Grid>
            </div>
          </Grid>       
          <Grid item xs={12} sm={7}  >
            <Grid style={{borderBottom: '1px solid #000', marginBottom: '10px'}}>
              <h2>Permissões</h2>
              {permissionValidationError && (
              <S.PermissionError title={permissionValidationError}>
                <FiAlertCircle color="#c53030" size={20} />
              </S.PermissionError>
            )}
            </Grid>
            
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                <h3 style={{fontWeight:'bold'}}>Dispositivos</h3>
                {permissionList.filter(p => p.name === "Dispositivos").map(permission => (
                  <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                    <FormControlLabel
                      key={permission.id}
                      control={
                        <Checkbox
                          checked={switchesState[permission.id]} 
                          onChange={handleChange(permission, updateRole)}
                          color="primary"
                        />
                      }
                      label={permission.description}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                <h3 style={{fontWeight:'bold'}}>Perfil</h3>
                {permissionList.filter(p => p.name === "Perfil").map(permission => (
                  <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                    <FormControlLabel
                      key={permission.id}
                      control={
                        <Checkbox
                          checked={switchesState[permission.id]} 
                          onChange={handleChange(permission, updateRole)}
                          color="primary"
                        />
                      }
                      label={permission.description}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                <h3 style={{fontWeight:'bold'}}>Usuários</h3>
                {permissionList.filter(p => p.name === "Usuários").map(permission => (
                  <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                    <FormControlLabel
                      key={permission.id}
                      control={
                        <Checkbox
                          checked={permission.description.toLowerCase() == "visualizar" || switchesState[permission.id]} 
                          onChange={handleChange(permission, updateRole)}
                          color="primary"
                          disabled={permission.description.toLowerCase() == "visualizar"}
                        />
                      }
                      label={permission.description}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12} sm={6} className={loader ? 'disabled' : 'input'}>
                <h3 style={{fontWeight:'bold'}}>Nível Hierárquico</h3>
                {permissionList.filter(p => p.name === "Nível Hierárquico").map(permission => (
                  <Grid item xs={12} key={permission.id} style={{paddingLeft: '15px'}}>
                    <FormControlLabel
                      key={permission.id}
                      control={
                        <Checkbox
                          checked={switchesState[permission.id]} 
                          onChange={handleChange(permission, updateRole)}
                          color="primary"
                        />
                      }
                      label={permission.description}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>


          </Grid>
          {canUpdate && (
            <Grid item xs={12}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100px', // Altura opcional, ajuste conforme necessário
              }}
            >
              {loader ? (
                <Button>Atualizando ...</Button>
              ) : (
                <>
                {canUpdate && (
                  <Button style={{ marginRight: '1rem', width: '130px' }}>
                    <RiUserAddLine size={20} />
                    Atualizar
                  </Button>
                )}

                {canDelete && (
                  <Button type="button" style={{ backgroundColor: '#dc3545', color: 'white', width: '130px' }} onClick={confirmDeleteRole}>
                    <RiDeleteBinLine size={20} />
                    Excluir
                  </Button>
                )}
                </>
              )}
            </Grid>  
          )}
        </Grid>
      </Form>
      </>
    ),
    [loader, RoleUpdateFormHandler, updateRole, isActiveRole, activeRoleChange, permissionList, switchesState, permissionValidationError, clientList, handleChange, isClientAdmin, canUpdate]
  );

  const searchForm = useMemo(() => {
    return (
      <Form onSubmit={searchHandle} ref={searchFormRef} className="search">
        <h3>
          <BiSearchAlt />
          Buscar
          <span> Perfis</span>
        </h3>
        <Input name="searchRole" type="text" placeholder="Nome do Perfil..." />
        {(isClientAdmin) && <Select name="searchClient" data={clientList} placeholder="Cliente..." />}
        <Button>pesquisar</Button>
      </Form>
    );
  }, [searchHandle, clientList, isClientAdmin]);

  const searchRoles = useMemo(() => {
    return (
      <>
        {canCreate && (
          <S.Register>
            <button
              className="register"
              type="button"
              onClick={() => {
                setSwitchesState(initialSwitchesState);
                
                let permissions: any = [];
                let permission: IPermissionCombo = permissionList.filter(p => p.name.toLowerCase() === "nível hierárquico" && p.description.toLowerCase() === "somente seu usuário")[0];
                if (permission)
                  permissions.push(permission)
                
                setCreateRole({ id: '', name: '', description: '', active: false, clientId: '', permissions} as IRole);
                setSwitchesState(prevState => ({ ...prevState, [permission.id]: true }));
                setRegister(true);
              }}
            >
              <RiUserAddLine size={20} />
              Cadastrar
            </button>
          </S.Register>
        )}
        
        <div className="results">
          <h3>Resultado:</h3>
          <ul>
            <button type="button" key="header">
              <p>Perfil</p>
              {isClientAdmin && (
                <p>Cliente</p>
              )}
              <p>Ativo/Inativo</p>
            </button>
          </ul>
          <ul>
            {roleList.length > 0 ? (
              roleList.map(role => (
                <button type="button" key={role.id}>
                  <p>{role.name}</p>
                  {isClientAdmin && (
                    <p>{role.client.name}</p>
                  )}
                  <p>{role.active ? <RiCheckboxCircleLine title="Ativo" color="#009933" /> : <RiCloseCircleLine title="Inativo" color="#87241b" />}</p>
                  {canUpdate && (
                    <FaEdit
                      onClick={() => {
                        getRoleById(role.id);
                      }}
                    />
                  )}
                </button>
              ))
            ) : (
              <h3 className="result">Nenhum Perfil encontrado</h3>
            )}
          </ul>
        </div>
      </>
    );
  }, [roleList, getRoleById, isClientAdmin, initialSwitchesState, canCreate,permissionList]);

  const backButton = useMemo(() => {
    return (
      <div className="close">
        <button
          type="button"
          onClick={() => {
            setPermissionValidationError(undefined);
            setRegister(false);
            setCreateRole(undefined);
            setUpdateRole(undefined);
          }}
        >
          <RiArrowLeftSLine size={20} />
          <p>Voltar</p>
        </button>
      </div>
    );
  }, []);

  return (
    <>
      {register && (
        <>
          <S.FormContainer>
            {backButton}
            <S.RoleForm>{RoleForm}</S.RoleForm>
          </S.FormContainer>
        </>
      )}
      {updateRole && (
        <>
          <S.FormContainer>
            {backButton}
            <S.RoleForm>
              {RoleUpdateForm}
            </S.RoleForm>
          </S.FormContainer>
        </>
      )}

      {!createRole && !updateRole && (
        <>
          <PageTitle sub="Perfis" title="Listagem" />
          <S.Container>
            <S.SearchForm show>{searchForm}</S.SearchForm>
            {/*  */}
            <S.SearchResults show> {searchRoles}</S.SearchResults>
          </S.Container>
        </>
      )}
      {showDeleteModal && (
        <S.DeleteModal>

            <div className="modal-content">
                <FiAlertTriangle size={30} color="#ff0000" />
                <p>Deseja excluir o perfil "{updateRole?.name}"?</p>
                <div className="modal-buttons">
              <Button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                style={{ marginRight: '1rem', backgroundColor: '#6c757d' }}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  handleDeleteRole();
                }}
                style={{ backgroundColor: '#dc3545', color: 'white' }}
              >
                <RiDeleteBinLine size={20} />
                Excluir
              </Button>
            </div>
          </div>


        </S.DeleteModal>
      )}
    </>
  );
};

export default Roles;
